/**
 * Utility functions for common operations.
 *
 * @module Utils
 * @namespace
 */
/**
 * Checks if an element with the given selector exists in the DOM.
 *
 * @param {string} selector - The CSS selector to query for.
 * @returns {boolean} - Returns `true` if an element with the specified selector exists, `false` otherwise.
 */
export const elementExists = (selector) => {
    return document.querySelector(selector) !== null;
};

/**
 * Truncates a string to a specified number of words.
 *
 * @param {string} content - The content to be truncated.
 * @param {number} wordLimit - The maximum number of words to include in the truncated content.
 * @returns {string} - The truncated string.
 */
export const truncateContent = (content, wordLimit) => {
    const strippedContent = content.replace(/(<([^>]+)>)/gi, '');
    const words = strippedContent.split(' ');
    if (words.length > wordLimit) {
        return words.slice(0, wordLimit).join(' ') + '...';
    }
    return strippedContent;
};

/**
 * Gets the prefix for Algolia search indexes based on the current environment.
 *
 * @returns {string} - The prefix for the Algolia search indexes.
 */
export const getIndexPrefixByEnvironment = () => {
    const environment = window.MyApp.environment;
    let prefix = '';

    if (environment === 'development') {
        prefix = 'dev_'
    } else if (environment === 'preprod') {
        prefix = 'pre_'
    } else if (environment === 'production') {
        prefix = 'prod_'
    }

    return prefix;
}

/**
 * Constructs the full name of an Algolia search index based on the current environment and provided index name.
 *
 * @param {string} indexName - The name of the index.
 * @returns {string} - The full name of the index.
 */
export const getFullIndexName = (indexName) => {
    const application = 'wbcsd_';
    return application + getIndexPrefixByEnvironment() + indexName;
}

/**
 * Finds the closest parent element with the specified class name.
 *
 * @param {Element} el - The element to start from.
 * @param {string} className - The class name to search for.
 * @returns {Element|null} - The closest parent element with the specified class name, or null if not found.
 */
export const findParentByClass = (el, className) => {
    while (el && el.nodeType === 1) {
        if (el.classList.contains(className)) {
            return el;
        }
        el = el.parentElement;
    }
    return null;
}

/**
 * Adjusts the heights of elements with the class `.fixed-title-height-js` to make them uniform.
 */
export const adjustTitleHeights = () => {
    function adjustTitleHeight() {
        const titles = document.querySelectorAll('.fixed-title-height-js');
        titles.forEach(title => title.style.height = '');

        let columns;
        const width = window.innerWidth;
        if (width >= 1024) {
            columns = 3;
        } else if (width >= 768) {
            columns = 2;
        } else {
            columns = 1;
        }

        let row = [];
        const rows = [row];
        titles.forEach((title, index) => {
            if (index % columns === 0 && index > 0) {
                row = [];
                rows.push(row);
            }
            row.push(title);
        });

        rows.forEach((row) => {
            let maxHeight = 0;
            row.forEach((title) => {
                maxHeight = Math.max(maxHeight, title.offsetHeight);
            });
            row.forEach((title) => {
                title.style.height = `${maxHeight}px`;
            });
        });
    }

    adjustTitleHeight();
    window.addEventListener("resize", adjustTitleHeight);
}

/**
 * Executes a callback function when a click occurs outside of the specified element.
 *
 * @param {Element} element - The element to detect clicks outside of.
 * @param {Function} callback - The callback function to execute.
 */
export const clickOutside  = (element, callback) => {
    const outsideClickListener = event => {
        if (!element.contains(event.target)) {
            callback();
            removeClickListener()
        }
    }

    const removeClickListener = () => {
        document.removeEventListener('click', outsideClickListener)
    }

    document.addEventListener('click', outsideClickListener)
}

/**
 * Checks if an object has any non-empty array properties.
 *
 * @param {Object} obj - The object to check.
 * @returns {boolean} - Returns `true` if the object has at least one non-empty array property, `false` otherwise.
 */
export const hasNonEmptyArray = (obj) => {
    if (obj === null) return false;

    for (const key in obj) {
        if (Array.isArray(obj[key]) && obj[key].length > 0) {
            return true;
        }
    }
    return false;
}

/**
 * Generates a secure random key of a specified length.
 *
 * @param {number} length - The desired length of the key.
 * @returns {string} - The generated secure random key.
 */
export const generateSecureRandomKey = (length) => {
    const array = new Uint8Array((length / 2));
    window.crypto.getRandomValues(array);
    return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Closes all dropdown elements with the class `.dropdown-content`.
 */
export const closeAllDropdowns = () => {
    const dropdowns = document.querySelectorAll('.dropdown-content');
    dropdowns.forEach(dropdown => {
        dropdown.classList.remove('show');
    })
}

/**
 * Checks if the current browser is an older version of Safari.
 *
 * Determines if the browser is Safari and then checks if its version is older than the specified minimum.
 *
 * @returns {boolean} Returns true if the browser is Safari and its version is older than the minimum required version, otherwise returns false.
 */
export const isOldSafariVersion = () => {
    const safariMinVersion = 16.5;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const version = navigator.userAgent.match(/Version\/(\d+\.\d+)/);

    return isSafari && version && parseFloat(version[1]) < safariMinVersion;
}

/**
 * Hides a specified HTML element if the browser is an older version of Safari.
 *
 * Uses `isOldSafariVersion` to determine the browser version. If it's an older version of Safari,
 * the function selects the specified HTML element and sets its display property to 'none', effectively hiding it.
 *
 * @param {string} elementSelector - The CSS selector for the HTML element to be hidden.
 */
export const hideCubeIfSafariNotSupported = (elementSelector) => {
    if (isOldSafariVersion()) {
        const cube = document.querySelector(elementSelector);
        cube.style.display = 'none';
    }
}

/**
 * Adds a query parameter to the current URL's search parameters and updates the browser's address bar.
 * This function modifies the current URL by adding a new query parameter or updating the value of an existing one.
 * It uses the `URL` and `URLSearchParams` Web APIs to manipulate query parameters and the `history.pushState` method
 * to update the browser's history stack without reloading the page.
 *
 * @param {string} key - The name of the query parameter to add or update.
 * @param {string} value - The value of the query parameter.
 */
export const addParamToUrl = (key, value) => {
    let currentURL = new URL(window.location.href);
    let searchParams = currentURL.searchParams;
    searchParams.set(key, value);
    window.history.pushState({}, '', currentURL.toString());
}

/**
 * Removes the "page" parameter from the URL.
 * @param {URL} url - The URL object to modify.
 * @returns {URL} - The modified URL object.
 */
export const removePageFromUrl = (url)  => {
    const hrefSegments = url.href.split('/');
    const pageIndex = hrefSegments.indexOf('page');
    if (pageIndex !== -1 && pageIndex < hrefSegments.length - 1 && !isNaN(hrefSegments[pageIndex + 1])) {
        hrefSegments.splice(pageIndex, 2);
    }
    url.href = hrefSegments.join('/');
    return url;
}

/**
 * Retrieves the URL for the admin-ajax.php file.
 *
 * This function constructs the URL for the admin-ajax.php file by appending it to the current origin.
 *
 * @returns {string} - The URL for the admin-ajax.php file.
 */
export const getAjaxUrl = () => {
    return new URL('/wp-admin/admin-ajax.php', window.location.origin).href;
}

/**
 * Scrolls to the specified DOM element identified by its ID.
 * The scrolling behavior is smooth, providing a gradual transition.
 *
 * @param {string} targetID - The ID of the target element to scroll to.
 */
export const scrollToDiv = (targetID) => {
    const element = document.getElementById(targetID);
    if (!element) return;

    const getOffsetTop = () => {
        const header = document.querySelector('#main-navbar');
        const headerHeight = header ? header.offsetHeight : 0;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        return elementPosition - headerHeight;
    };

    const initialOffset = getOffsetTop();
    window.scrollTo({
        top: initialOffset,
        behavior: 'smooth'
    });

    setTimeout(() => {
        const retryOffset = getOffsetTop();
        if (Math.abs(retryOffset - initialOffset) > 1) {
            window.scrollTo({
                top: retryOffset,
                behavior: 'smooth'
            });
        }
    }, 300);
}

/**
 * Retrieves the content type from the URL search parameters.
 * This function extracts the 'content_type' query parameter from the current URL and returns its value.
 *
 * @returns {string|null} - The value of the 'content_type' query parameter, or null if it doesn't exist.
 */
export const getContentTypeFromUrl = () => {
    let currentUrl = window.location.href;
    let parsedUrl = new URL(currentUrl);
    let params = parsedUrl.searchParams;
    return params.get('content_type');
}